import {
  ResponsiveContainer,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  Area,
  AreaChart,
  ReferenceLine,
} from 'recharts';
import { useTranslation } from 'react-i18next';

import type { RevenueGraphItem } from '../../api/dashboard.api';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function formatMonth(yearMonth: string) {
  const parts = yearMonth.split('-');
  const monthIndex = parseInt(parts[1], 10) - 1;
  return MONTHS[monthIndex] ?? yearMonth;
}

/** Axis ticks: $1.2k above a thousand, plain dollars below — $300 must read as $300. */
function formatCurrency(value: number) {
  if (Math.abs(value) >= 1000) {
    const k = value / 1000;
    return `$${Number.isInteger(k) ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return `$${Math.round(value)}`;
}

/**
 * Round up to a readable axis maximum at the data's own order of magnitude
 * (300 → 400, 3 000 → 4 000, 45 000 → 50 000).
 *
 * ⚠ The old axis was `ceil(max × 1.2 / 10000) × 10000` over a max that included
 * the **yearly** plan, so with a $500,000 plan the domain became $0–$600k and
 * actual revenue of $300–$3,000 drew flat on the zero line — the chart the
 * 2026-07-26 review called unreadable (§5.8). The yearly plan is a full-year
 * total and no longer stretches a monthly axis.
 */
function niceMax(value: number): number {
  if (!Number.isFinite(value) || value <= 0) return 1000;
  const padded = value * 1.2;
  const magnitude = 10 ** Math.floor(Math.log10(padded));
  return Math.ceil(padded / magnitude) * magnitude;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{ name: string; value: number; dataKey: string }>;
  label?: string;
  yearlyPlanAmount?: number;
  yearlyPlanLabel?: string;
}

function CustomTooltip({ active, payload, label, yearlyPlanAmount, yearlyPlanLabel }: CustomTooltipProps) {
  if (!active || !payload?.length) return null;
  const actual = payload.find(p => p.dataKey === 'actual');
  const plan = payload.find(p => p.dataKey === 'plan');
  const ratio = actual?.value && plan?.value ? actual.value / plan.value : null;
  const status = ratio == null ? null : ratio >= 1 ? '🟢 On Track' : ratio >= 0.9 ? '🟡 Risk' : '🔴 Behind';

  return (
    <div className="bg-slate-900 text-white rounded-lg p-3 text-xs shadow-lg">
      <p className="font-semibold text-sm mb-1">{label}</p>
      {payload.map(p => (
        <p key={p.dataKey} className="mt-0.5" style={{ color: p.dataKey === 'plan' ? '#94a3b8' : '#60a5fa' }}>
          {p.name}: ${(p.value || 0).toLocaleString()}
        </p>
      ))}
      {yearlyPlanAmount != null && (
        <p className="mt-0.5" style={{ color: '#ef4444' }}>
          {yearlyPlanLabel}: ${yearlyPlanAmount.toLocaleString()}
        </p>
      )}
      {status && <p className="mt-1">{status}</p>}
    </div>
  );
}

interface RevenueChartProps {
  data: RevenueGraphItem[] | undefined;
  isLoading: boolean;
  monthlyPlan?: number;
  yearlyPlanAmount?: number;
}

export function RevenueChart({ data, isLoading, monthlyPlan, yearlyPlanAmount }: RevenueChartProps) {
  const { t } = useTranslation();

  /**
   * D-06 — "The chart legend lists both Actual and Plan. Only the Actual series
   * is drawn."
   *
   * The plan series was conditional on a `monthlyPlan` prop that is derived
   * (`stats.plan / 12`) and silently becomes 0 or NaN whenever the period's
   * prorated plan is unavailable — while the legend entry was keyed on the
   * looser `!= null`, so the caption could promise a series the chart never
   * drew. The monthly plan is now resolved once, validated as a positive finite
   * number, and the SAME value gates both the legend and the line: either both
   * appear or neither does.
   */
  const resolvedMonthlyPlan =
    monthlyPlan != null && Number.isFinite(monthlyPlan) && monthlyPlan > 0
      ? monthlyPlan
      : yearlyPlanAmount != null && yearlyPlanAmount > 0
        ? yearlyPlanAmount / 12
        : null;
  const hasPlan = resolvedMonthlyPlan != null;

  const chartData = (data ?? []).map(item => ({
    month: formatMonth(item.month),
    actual: item.revenue,
    // Explicitly `undefined` (not null) when absent, so Recharts skips the
    // series rather than plotting a zero line that reads as "plan = $0".
    plan: hasPlan ? resolvedMonthlyPlan : undefined,
  }));

  // Single data point renders as a dot in Recharts — duplicate it so a flat line is drawn instead.
  if (chartData.length === 1) {
    chartData.unshift({ ...chartData[0], month: '' });
  }

  // Domain follows the monthly series only — see `niceMax`.
  const maxValue = Math.max(
    ...(data ?? []).map(d => d.revenue),
    resolvedMonthlyPlan ?? 0,
    0,
  );
  const yMax = niceMax(maxValue);
  // The yearly-plan marker is drawn only when it actually falls inside the
  // chart; otherwise it would either be invisible or flatten everything else.
  const showYearlyPlanLine = yearlyPlanAmount != null && yearlyPlanAmount <= yMax;

  const legendPayload = [
    { value: t('dashboard.actual'), type: 'circle' as const, color: '#0069D1' },
    ...(hasPlan
      ? [{ value: t('dashboard.planned'), type: 'circle' as const, color: '#94a3b8' }]
      : []),
    ...(showYearlyPlanLine
      ? [{ value: t('dashboard.yearlyPlan'), type: 'circle' as const, color: '#ef4444' }]
      : []),
  ];

  if (isLoading) {
    return <div className="h-[280px] flex items-center justify-center text-slate-400 text-sm">{t('loading')}</div>;
  }

  return (
    <ResponsiveContainer width="100%" height={280}>
      <AreaChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
        <defs>
          <linearGradient id="actualGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="#0069D1" stopOpacity={0.08} />
            <stop offset="95%" stopColor="#0069D1" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
        <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} axisLine={false} tickLine={false} />
        <YAxis
          domain={[0, yMax]}
          tickFormatter={formatCurrency}
          tick={{ fontSize: 11, fill: '#94a3b8' }}
          axisLine={false}
          tickLine={false}
          tickCount={6}
        />
        <Tooltip content={<CustomTooltip yearlyPlanAmount={yearlyPlanAmount} yearlyPlanLabel={t('dashboard.yearlyPlan')} />} />
        <Legend
          align="right"
          verticalAlign="top"
          iconType="circle"
          iconSize={8}
          wrapperStyle={{ fontSize: 11, color: '#64748b', paddingBottom: 8 }}
          payload={legendPayload}
        />
        {showYearlyPlanLine && (
          <ReferenceLine
            y={yearlyPlanAmount}
            stroke="#ef4444"
            strokeWidth={2}
            strokeDasharray="6 4"
          />
        )}
        <Area
          type="monotone"
          dataKey="actual"
          name={t('dashboard.actual')}
          stroke="#0069D1"
          strokeWidth={2}
          fill="url(#actualGradient)"
          dot={{ r: 3, fill: '#0069D1', stroke: '#fff', strokeWidth: 2 }}
          activeDot={{ r: 5 }}
          connectNulls={false}
        />
        {hasPlan && (
          <Line
            type="monotone"
            dataKey="plan"
            name={t('dashboard.planned')}
            stroke="#94a3b8"
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            activeDot={{ r: 4, fill: '#94a3b8' }}
            // The plan is a flat monthly target, so it must span the full axis
            // even across months with no revenue row.
            connectNulls
            isAnimationActive={false}
          />
        )}
      </AreaChart>
    </ResponsiveContainer>
  );
}
